import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { cloneDeep, find, isNil, omit, clone, get, assign } from 'lodash';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { UploadFile } from 'ngx-uploader';
import { IFormTab, IPublicForm } from './model/form.model';
import { IField } from './model/field';
import  moment from 'moment';

export interface ISubmitResponse {
  success: boolean;
  errors?: any[];
  tip?: string;
}

export interface IUploadUrlResponse {
  uploadUrl: string;
  reference: string;
}

export interface AcknowledgmentInfo {
  title: string;
  message: string;
}

@Injectable({
  providedIn: 'root',
})
export class FormService {
  private http = inject(HttpClient);

  acknowledgmentScreenInfo: BehaviorSubject<AcknowledgmentInfo> =
    new BehaviorSubject<AcknowledgmentInfo>({ title: '', message: '' });

  submitForm$(
    formTip: string,
    unfilteredFormData: any,
    typeFields: IField[]
  ): Observable<ISubmitResponse> {
    // Incredibly hacky... *sigh*
    const propertiesToOmit = [];
    Object.keys(unfilteredFormData).forEach((element) => {
      // if property is uuid || if value equals null or undefined
      if (
        /^[0-9a-f]{8}-([0-9a-f]{4}-){3}[0-9a-f]{12}$/i.test(element) ||
        isNil(unfilteredFormData[element])
      ) {
        propertiesToOmit.push(element); // we will remove null fields from the dto
      } else {
        // if non-null .. do the extra handling for date & date-time fields
        const fieldInfo = typeFields.find((field) => element === field.$tip);
        if (
          fieldInfo &&
          fieldInfo.format === 'date-time' &&
          moment(unfilteredFormData[element]).isValid()
        ) {
          unfilteredFormData[element] =
            fieldInfo.isDateOnly === true
              ? moment(unfilteredFormData[element]).startOf('day').format() // for date .. set hh:mm to 00:00
              : moment(unfilteredFormData[element]).format();
        }
      }
    });

    const filteredFormData = omit(
      cloneDeep(unfilteredFormData),
      propertiesToOmit
    );

    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'en-namespace': environment.namespace,
        'recaptcha-v3-token': '',
      }),
    };
    const url =
      environment.externalServiceUrl +
      'v2/submitForm/' +
      encodeURIComponent(formTip);
    return this.http.post<ISubmitResponse>(url, filteredFormData, httpOptions);
  }


  getForm$(formTip: string): Observable<IPublicForm | null> {
    let url = '';
    const form = find(environment.forms, { tip: formTip });
    if (form) {
      return of(form);
    }
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'en-namespace': environment.namespace,
      }),
    };
    url =
      environment.externalServiceUrl +
      'formSchema/' +
      encodeURIComponent(formTip);
    return this.http
      .get<IPublicForm>(url, httpOptions)
      .pipe(catchError((err) => of(null)));
  }

  filterField(field: any) {
    return (
      [
        'section-header',
        'input',
        'multi-line-text',
        'email-address',
        'phone-number',
        'text-content',
        'checkbox',
        'number',
        'list-items-select',
      ].indexOf(field.type) > -1
    );
  }

  getUploadUrl$(file: UploadFile): Observable<IUploadUrlResponse> {
    const httpOptions = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'en-namespace': environment.namespace,
        'recaptcha-v3-token': '',
      }),
    };
    const data = {
      filename: file.name,
      mimeType: file.type,
    };
    const url = environment.externalServiceUrl + 'uploadUrl';
    return this.http.post<IUploadUrlResponse>(url, data, httpOptions);
  }

  /*
   * this will merge properties from the mapped object type to the form-field
   */
  mergeObjectSchemaWithFormFields(
    tabs: IFormTab[],
    typeFields: IField[]
  ): IField[] {
    const toReturn = clone(typeFields);

    for (const tab of tabs) {
      const rows = get(tab, 'rows', []);
      for (const row of rows) {
        const fields = get(row, 'fields', []);
        for (const field of fields) {
          const contextTypeTip = get(
            field,
            'templateOptions.contextTypeTip',
            null
          );
          if (contextTypeTip) {
            const typeField = toReturn.find(
              (item) => item.$tip === contextTypeTip
            );
            const propertiesFromObjectSchema = get(
              field,
              'objectSchemaProperties',
              null
            );
            if (typeField && propertiesFromObjectSchema) {
              assign(typeField, propertiesFromObjectSchema);
            }
          }
        }
      }
    }
    return toReturn;
  }
}
